import React from 'react';
import { motion } from 'framer-motion';
import { Cpu, HardDrive, Activity, FileText, AlertTriangle } from 'lucide-react';
import { ConfidenceMeter } from './ConfidenceMeter';
import { ToolsList } from './ToolsList';
import { SafetyBadge } from './SafetyBadge';
import { cn } from '../../lib/utils';

export const AnalysisResult = ({
  deviceInfo,
  diagnosis,
  safetyCheck,
  className,
  isLoading = false
}) => {
  if (isLoading) {
    return (
      <div className="w-full max-w-5xl mx-auto space-y-6 animate-pulse px-4">
        <div className="h-8 bg-surface-highlight w-1/4" />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-surface h-64" />
          <div className="bg-surface h-64" />
        </div>
        <div className="bg-surface h-32 w-full" />
      </div>
    );
  }

  return (
    <div className={cn('w-full max-w-5xl mx-auto space-y-4 px-4', className)} data-testid="analysis-result">
      {/* 1. Header Section */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="font-heading text-2xl font-bold uppercase tracking-tight text-white">
            ANALYSIS COMPLETE
          </h2>
          <p className="text-text-muted text-sm mt-1 font-mono uppercase tracking-wider">AI-powered diagnosis results</p>
        </div>
        {safetyCheck && (
          <SafetyBadge risk={diagnosis?.safety_risk} mode={safetyCheck.mode} />
        )}
      </div>

      {/* 2. Top Grid: Device Info & Diagnosis */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-stretch">
        {/* Device Identified Card */}
        {deviceInfo && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-surface border border-border p-5 hud-brackets relative flex flex-col justify-between"
          >
            <div>
              <div className="flex items-center gap-2 mb-6">
                <HardDrive className="w-4 h-4 text-secondary" />
                <span className="font-heading text-xs uppercase tracking-[0.2em] text-secondary font-bold">
                  Device Identified
                </span>
              </div>
              <div className="space-y-4">
                <div>
                  <span className="font-mono text-[10px] text-text-muted block uppercase tracking-widest mb-1">TYPE</span>
                  <span className="text-white text-lg font-medium">{deviceInfo.device_type || 'Unknown'}</span>
                </div>
                <div>
                  <span className="font-mono text-[10px] text-text-muted block uppercase tracking-widest mb-1">BRAND</span>
                  <span className="text-white text-lg font-medium">{deviceInfo.brand || 'Unknown'}</span>
                </div>
                <div>
                  <span className="font-mono text-[10px] text-text-muted block uppercase tracking-widest mb-1">MODEL</span>
                  <span className="text-white text-lg font-mono font-medium">{deviceInfo.model || 'Unknown'}</span>
                </div>
              </div>
            </div>

            {/* Confidence Bar (Aligned to bottom of card) */}
            <div className="mt-6 pt-4 border-t border-border/50">
              <div className="flex items-center justify-between mb-2">
                <span className="font-mono text-[10px] text-text-muted uppercase tracking-widest">ID Confidence</span>
                <span className="font-mono text-sm text-secondary font-bold">{Math.round((deviceInfo.confidence || 0) * 100)}%</span>
              </div>
              <div className="h-1 bg-surface-highlight w-full overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${(deviceInfo.confidence || 0) * 100}%` }}
                  className="h-full bg-secondary"
                />
              </div>
            </div>
          </motion.div>
        )}

        {/* Diagnosis Card */}
        {diagnosis && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-surface border border-border p-5 flex flex-col"
          >
            <div className="flex items-center gap-2 mb-6">
              <Cpu className="w-4 h-4 text-primary" />
              <span className="font-heading text-xs uppercase tracking-[0.2em] text-primary font-bold">
                Diagnosis
              </span>
            </div>
            <div className="space-y-4 flex-1">
              <div>
                <span className="font-mono text-[10px] text-text-muted block uppercase tracking-widest mb-1">BROKEN PART</span>
                <span className="text-white text-lg font-medium">{diagnosis.broken_part || 'Unknown'}</span>
              </div>
              <div>
                <span className="font-mono text-[10px] text-text-muted block uppercase tracking-widest mb-1">DIAGNOSIS</span>
                <p className="text-text-secondary text-sm leading-relaxed">{diagnosis.diagnosis || 'No diagnosis available'}</p>
              </div>
              {diagnosis.reasoning_summary && (
                <div className="bg-surface-highlight/40 p-4 border-l-2 border-primary/30">
                  <p className="text-text-muted text-xs font-mono leading-relaxed">{diagnosis.reasoning_summary}</p>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </div>

      {/* 3. Bottom Stack: Symptoms, Safety, and Recommendation */}
      <div className="flex flex-col gap-4">
        {/* Tools (Optional placement inside or below grid) */}
        {diagnosis?.tools_required?.length > 0 && (
          <motion.div className="bg-surface border border-border p-5">
            <div className="flex items-center gap-2 mb-4">
              <span className="font-heading text-[10px] uppercase tracking-widest text-text-muted font-bold">Tools Required</span>
            </div>
            <ToolsList tools={diagnosis.tools_required} />
          </motion.div>
        )}

        {/* Observed Symptoms */}
        {deviceInfo?.observed_symptoms && (
          <motion.div className="bg-surface border border-border/60 p-5">
            <div className="flex items-center gap-2 mb-3">
              <Activity className="w-4 h-4 text-warning" />
              <span className="font-heading text-[11px] uppercase tracking-widest text-warning font-bold">Observed Symptoms</span>
            </div>
            <p className="text-text-secondary text-sm leading-relaxed">{deviceInfo.observed_symptoms}</p>
          </motion.div>
        )}

        {/* Safety Assessment */}
        {safetyCheck && (
          <motion.div className={cn("border p-5 bg-surface", safetyCheck.repair_allowed ? "border-success/30" : "border-warning/30")}>
            <div className="flex items-start gap-4">
              <FileText className={cn("w-5 h-5 mt-1", safetyCheck.repair_allowed ? "text-success" : "text-warning")} />
              <div className="flex-1">
                <span className={cn("font-heading text-[11px] uppercase tracking-widest block mb-2 font-bold", safetyCheck.repair_allowed ? "text-success" : "text-warning")}>Safety Assessment</span>
                <p className="text-text-secondary text-sm leading-relaxed">{safetyCheck.safety_notice}</p>
              </div>
            </div>
          </motion.div>
        )}

        {/* Critical Repair Block (Centered at Bottom) */}
        {!safetyCheck?.repair_allowed && (
          <motion.div className="bg-warning/10 border border-warning/30 p-6 flex flex-col items-center text-center gap-2">
            <div className="flex items-center gap-2 text-warning">
              <AlertTriangle className="w-5 h-5" />
              <span className="font-heading text-xs uppercase font-bold tracking-[0.2em]">Repair Not Recommended</span>
            </div>
            <p className="text-text-muted text-[11px] max-w-md uppercase tracking-wide leading-relaxed">
              Based on our safety analysis, this repair should be performed by a professional technician.
            </p>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default AnalysisResult;